'use client';

import { Card } from '@repo/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { ScatterPlot } from '../common/scatter-plot';
import jStat from 'jstat';

export interface CorrelationResult {
  correlationCoefficient: number;
  rSquared: number;
  pValue: number;
  sampleSize: number;
  data1: number[];
  data2: number[];
  xLabel?: string;
  yLabel?: string;
}

interface CorrelationCalculatorProps {
  visualizationData: CorrelationResult | null;
}

export function CorrelationCalculator({ visualizationData: result }: CorrelationCalculatorProps) {
  if (!result) {
    return null;
  }

  const { correlationCoefficient, rSquared, pValue, sampleSize, data1, data2, xLabel, yLabel } = result;

  // Create scatter plot data points
  const scatterData = data1.map((x, i) => [x, data2[i]] as [number, number]);

  // Calculate regression line
  const xMin = Math.min(...data1);
  const xMax = Math.max(...data1);
  
  // Calculate regression line points
  const slope = correlationCoefficient * (Math.sqrt(jStat.variance(data2)) / Math.sqrt(jStat.variance(data1)));
  const intercept = jStat.mean(data2) - slope * jStat.mean(data1);
  const regressionLine = [
    [xMin, slope * xMin + intercept],
    [xMax, slope * xMax + intercept]
  ] as [number, number][];

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Correlation analysis results</h3>
          <div className="space-y-4">
            <div>
              <p className="font-medium">Correlation coefficient (r):</p>
              <p>{correlationCoefficient.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">Coefficient of determination (R²):</p>
              <p>{rSquared.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">P-value:</p>
              <p>
                {pValue.toFixed(4)}
                <span className="text-sm text-gray-600 ml-2">
                  ({pValue < 0.05 ? 'Statistically significant' : 'Not statistically significant'})
                </span>
              </p>
            </div>
            <div>
              <p className="font-medium">Sample size:</p>
              <p>{sampleSize}</p>
            </div>
            <div>
              <p className="font-medium">Statistical interpretation:</p>
              <div className="space-y-2 mt-1">
                <p className="text-sm text-gray-600">
                  Strength and direction:
                  <span className="text-black font-medium ml-2">
                    {correlationCoefficient > 0.7
                      ? 'Strong positive correlation'
                      : correlationCoefficient > 0.3
                      ? 'Moderate positive correlation'
                      : correlationCoefficient > -0.3
                      ? 'Weak or no correlation'
                      : correlationCoefficient > -0.7
                      ? 'Moderate negative correlation'
                      : 'Strong negative correlation'}
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  {pValue < 0.05 
                    ? `There is strong statistical evidence (p < 0.05) that the correlation between ${xLabel} and ${yLabel} is not due to random chance.`
                    : `There is not enough statistical evidence (p ≥ 0.05) to conclude that the correlation between ${xLabel} and ${yLabel} is not due to random chance.`
                  }
                </p>
                <p className="text-sm text-gray-600">
                  The coefficient of determination (R² = {rSquared.toFixed(4)}) indicates that {(rSquared * 100).toFixed(1)}% of the variance in {yLabel} can be explained by {xLabel}.
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Scatter plot</h3>
          <ScatterPlot
            data={scatterData}
            xLabel={xLabel || 'Independent Variable (X)'}
            yLabel={yLabel || 'Dependent Variable (Y)'}
            title={`Correlation Plot (r = ${correlationCoefficient.toFixed(4)})`}
            trendLine={{
              data: regressionLine,
              color: '#4d79ff'
            }}
          />
        </Card>
      </div>
    </ScrollableContainer>
  );
} 